import { useCallback, useEffect, useState } from "react";
import { Handshake } from "lucide-react";
import {
  Button,
  EmptyState,
  SkeletonList,
  StatusBadge,
  Textarea,
  Timestamp,
  useToast,
  type StatusTone,
} from "@/design-system/components/ui";
import { PortalApiError } from "../api/client";
import { brokerAffiliationsApi, type BrokerAffiliationRequest } from "../api/brokerAffiliations";

// Brokerage / Sub-Broker PR-7 — the broker-side "Affiliation Requests" surface
// (two-sided consent). A brokerage (or staff on its behalf) proposes an
// affiliation; the broker accepts (joining the firm, leaving any current one —
// D13 single-active) or declines here. Type-agnostic disclosure: the junior
// "the brokerage becomes your financial recipient" copy is PR-8. Portal-local
// (design-system kit only); no React Query — a successful action re-loads.

const STATUS_TONE: Record<string, StatusTone> = {
  Active: "ok",
  Proposed: "warn",
};

function errorMessage(err: unknown, fallback: string): string {
  if (err instanceof PortalApiError) return err.message;
  if (err instanceof Error) return err.message;
  return fallback;
}

export function BrokerAffiliationsTab() {
  const toast = useToast();
  const [rows, setRows] = useState<BrokerAffiliationRequest[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [declineOpenId, setDeclineOpenId] = useState<string | null>(null);
  const [declineReason, setDeclineReason] = useState("");

  const loadList = useCallback(async () => {
    const data = await brokerAffiliationsApi.list();
    setRows(data);
  }, []);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const data = await brokerAffiliationsApi.list();
        if (!cancelled) setRows(data);
      } catch (err) {
        if (!cancelled) setError(errorMessage(err, "Couldn't load your affiliation requests."));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleAccept(affiliationId: string) {
    setBusyId(affiliationId);
    try {
      await brokerAffiliationsApi.accept(affiliationId);
      await loadList();
      toast.success("Affiliation accepted.");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't accept this request. Try again."));
    } finally {
      setBusyId(null);
    }
  }

  async function handleDecline(affiliationId: string) {
    setBusyId(affiliationId);
    try {
      await brokerAffiliationsApi.decline(affiliationId, declineReason);
      setDeclineOpenId(null);
      setDeclineReason("");
      await loadList();
      toast.success("Affiliation declined.");
    } catch (err) {
      toast.error(errorMessage(err, "Couldn't decline this request. Try again."));
    } finally {
      setBusyId(null);
    }
  }

  if (error) {
    return (
      <p className="text-small text-status-error" role="alert">
        {error}
      </p>
    );
  }
  if (!rows) {
    return <SkeletonList rows={3} data-testid="broker-affiliations-loading" />;
  }

  const current = rows.find((r) => r.status === "Active") ?? null;
  const pending = rows.filter((r) => r.status === "Proposed");

  if (rows.length === 0) {
    return (
      <EmptyState
        icon={Handshake}
        title="No affiliation requests"
        description="When a brokerage invites you to join their firm, the request will appear here for you to accept or decline."
      />
    );
  }

  return (
    <div className="flex flex-col gap-space-4">
      {current && (
        <section
          aria-label="Current affiliation"
          className="rounded-lg border border-border-light p-space-3"
          data-testid="broker-affiliation-current"
        >
          <div className="flex flex-wrap items-center justify-between gap-space-2">
            <div>
              <p className="m-0 text-small font-semibold text-onyx">{current.brokerageName}</p>
              <p className="m-0 text-caption text-gray-mid">
                Your current brokerage
                {current.acceptedAt ? (
                  <>
                    {" "}
                    · joined <Timestamp value={current.acceptedAt} />
                  </>
                ) : null}
              </p>
            </div>
            <StatusBadge tone={STATUS_TONE.Active}>Active</StatusBadge>
          </div>
        </section>
      )}

      {pending.length === 0 ? (
        <EmptyState
          icon={Handshake}
          title="No pending requests"
          description="You have no brokerage invitations waiting. New requests will appear here."
        />
      ) : (
        <section aria-label="Pending affiliation requests" className="flex flex-col gap-space-3">
          {pending.map((req) => {
            const leavingFirm = current && current.brokerageId !== req.brokerageId ? current.brokerageName : null;
            const leavingSuffix = leavingFirm ? (
              <>
                {" "}
                You'll leave your current firm, <strong>{leavingFirm}</strong>.
              </>
            ) : null;
            const isBusy = busyId === req.affiliationId;
            const declineOpen = declineOpenId === req.affiliationId;
            return (
              <div
                key={req.affiliationId}
                className="rounded-lg border border-border-light p-space-3"
                data-testid={`broker-affiliation-request-${req.affiliationId}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-space-2">
                  <div>
                    <p className="m-0 text-small font-semibold text-onyx">{req.brokerageName}</p>
                    <p className="m-0 text-caption text-gray-mid">
                      Invited you to join · <Timestamp value={req.proposedAt} />
                    </p>
                  </div>
                  <StatusBadge tone={STATUS_TONE.Proposed}>Proposed</StatusBadge>
                </div>

                {/* PR-8 — type-aware financial disclosure. A Junior broker is paid
                    through the firm (Lever C); an Independent broker keeps the
                    generic roster copy (byte-identical to PR-7). The leaving-firm
                    suffix (D13) is shared by both. */}
                <p className="mt-space-2 text-small text-onyx">
                  {req.isJunior ? (
                    <>
                      As a junior broker, Phoenix pays <strong>{req.brokerageName}</strong> for your
                      deals, and {req.brokerageName} pays you off-platform. Accepting adds you to
                      their roster.
                    </>
                  ) : (
                    <>Accepting adds you to {req.brokerageName}'s roster.</>
                  )}
                  {leavingSuffix}
                </p>

                {declineOpen ? (
                  <div className="mt-space-3 flex flex-col gap-space-2">
                    <Textarea
                      value={declineReason}
                      onChange={(e) => setDeclineReason(e.target.value)}
                      placeholder="Reason (optional)…"
                      rows={2}
                      disabled={isBusy}
                      aria-label="Reason for declining"
                      data-testid="broker-affiliation-decline-reason"
                    />
                    <div className="flex flex-wrap gap-space-2">
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={() => void handleDecline(req.affiliationId)}
                        disabled={isBusy}
                        loading={isBusy}
                        data-testid="broker-affiliation-decline-confirm"
                      >
                        Confirm decline
                      </Button>
                      <Button
                        type="button"
                        variant="text"
                        size="sm"
                        onClick={() => {
                          setDeclineOpenId(null);
                          setDeclineReason("");
                        }}
                        disabled={isBusy}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-space-3 flex flex-wrap gap-space-2">
                    <Button
                      type="button"
                      variant="primary"
                      size="sm"
                      onClick={() => void handleAccept(req.affiliationId)}
                      disabled={isBusy}
                      loading={isBusy}
                      data-testid="broker-affiliation-accept"
                    >
                      Accept
                    </Button>
                    <Button
                      type="button"
                      variant="secondary"
                      size="sm"
                      onClick={() => {
                        setDeclineOpenId(req.affiliationId);
                        setDeclineReason("");
                      }}
                      disabled={isBusy}
                      data-testid="broker-affiliation-decline"
                    >
                      Decline
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </section>
      )}
    </div>
  );
}
